import type { MaterialSlot } from '@ds-mgnl/marketing/model-viewer';
import { chipUrl, slugify } from '../../../components/ProductDetail';
import type {
	AssetDimension,
	FamilyAssetMap,
	MaterialGroup,
	MaterialOption,
	MaterialTextureConfig,
	Product3dConfig,
	ProductAttributeValue,
	ProductResponse,
} from '../../../services/productApi';
import {
	FIXED_REPEAT_INCHES_BY_DIMENSION,
	MATERIAL_SLOTS_BY_DIMENSION,
	STATIC_MATERIALS,
} from '../../../services/productApi/productModelConfig';

export interface FabricOverride {
	dimension: AssetDimension;
	/** Applied to seat slots (and back slots when they mirror the seat). */
	seatValueSlug: string;
	/** Applied to back slots; omitted when the back mirrors the seat. */
	backValueSlug?: string;
}

const BACK_FABRIC_DIMENSION = 'back-fabric';

const FABRIC_REPEAT_FALLBACK_INCHES = 8;

export function fabricTextureFromDimension(
	dimension: AssetDimension,
	valueSlug: string,
): MaterialTextureConfig | undefined {
	const swatch = dimension.values[valueSlug]?.swatch;
	if (!swatch?.url) return undefined;

	return {
		url: swatch.url,
		repeatWidthInches: swatch.uv?.w ?? FABRIC_REPEAT_FALLBACK_INCHES,
		repeatHeightInches: swatch.uv?.h ?? FABRIC_REPEAT_FALLBACK_INCHES,
	};
}

/**
 * The first value slug in a fabric dimension whose swatch resolves to a usable
 * texture. Used to default the picker to a value that actually paints the model
 * rather than the leading value, which may have no swatch and would leave the
 * fabric slot blank in the visualizer. Returns undefined when nothing resolves.
 */
export function firstResolvingFabricValueSlug(
	dimension: AssetDimension | null | undefined,
): string | undefined {
	if (!dimension) return undefined;
	return Object.keys(dimension.values).find(
		(slug) => !!fabricTextureFromDimension(dimension, slug),
	);
}

/**
 * Whether the built materials paint any selectable surface (seat/back fabric,
 * frame finish, nailheads) rather than only the always-on {@link STATIC_MATERIALS}
 * (felt, frame, feet, ...). A model with only static materials reads as a blank
 * visualizer, so callers can fall back to a product image instead of defaulting
 * the gallery to it.
 */
export function hasSelectableSurface(
	materials: Record<string, MaterialTextureConfig> | undefined,
): boolean {
	if (!materials) return false;
	const staticSlots = new Set(Object.keys(STATIC_MATERIALS));
	return Object.keys(materials).some((slot) => !staticSlots.has(slot));
}

const dimensionKeywords = [
	'height',
	'width',
	'depth',
	'thickness',
	'diameter',
	'length',
	'size',
];

export function partitionAttributes(
	attributes: Record<string, ProductAttributeValue[]>,
): {
	dimensions: Record<string, ProductAttributeValue[]>;
	other: Record<string, ProductAttributeValue[]>;
} {
	const dimensions: Record<string, ProductAttributeValue[]> = {};
	const other: Record<string, ProductAttributeValue[]> = {};

	for (const [key, values] of Object.entries(attributes)) {
		const isDimension = dimensionKeywords.some((kw) =>
			key.toLowerCase().includes(kw),
		);
		if (isDimension) {
			dimensions[key] = values;
		} else {
			other[key] = values;
		}
	}

	return { dimensions, other };
}

/**
 * Material-dimension selections a familyless product fixes in its own attributes
 * (e.g. "Frame Finish": "Walnut"), keyed dimension-slug -> value-slug. Only
 * known dimensions (MATERIAL_SLOTS_BY_DIMENSION) are kept; "N/A"-style sentinels
 * are dropped since there is no asset map to fetch for them (would 404).
 */
const NON_VALUE_SLUGS = new Set(['n-a', 'na', 'none']);

export function materialAttributeSelections(
	product: ProductResponse | null | undefined,
): Record<string, string> {
	const selections: Record<string, string> = {};
	const attributes = product?.attributes;
	if (!attributes) return selections;

	for (const [name, values] of Object.entries(attributes)) {
		const dimensionSlug = slugify(name);
		if (!(dimensionSlug in MATERIAL_SLOTS_BY_DIMENSION)) continue;

		const raw = values?.[0]?.value ?? values?.[0]?.valueText;
		if (!raw) continue;

		const valueSlug = slugify(raw);
		if (!valueSlug || NON_VALUE_SLUGS.has(valueSlug)) continue;

		selections[dimensionSlug] = valueSlug;
	}

	return selections;
}

/**
 * Whether the model's discovered slots cover the given dimension, matched by
 * extras key (slot.key "Back Fabric" -> "back-fabric") or GLB material name.
 * Drives whether a separate back-fabric selector is shown.
 *
 * back-fabric is matched by key ONLY: `mat_fabric` is the back on dual models
 * but the all-over fabric on single ones, so name-matching it would show a
 * spurious back selector on single-fabric pieces.
 */
export function modelHasDimensionSlot(
	slots: MaterialSlot[] | null | undefined,
	dimensionSlug: string,
): boolean {
	if (!slots?.length) return false;

	const matchesByKey = slots.some((slot) => slugify(slot.key) === dimensionSlug);
	if (matchesByKey) return true;

	if (dimensionSlug === BACK_FABRIC_DIMENSION) return false;

	const expectedNames = (MATERIAL_SLOTS_BY_DIMENSION[dimensionSlug] ?? []).map(
		(name) => name.toLowerCase(),
	);
	return slots.some((slot) =>
		slot.materialNames.some((name) => expectedNames.includes(name.toLowerCase())),
	);
}

const SEAT_FABRIC_DIMENSION = 'seat-fabric';

/**
 * The value slug a product uses for a group, plus the dimension it belongs to.
 * A fabric group with no dedicated selection falls back to the seat fabric under
 * the seat-fabric dimension, so a back with no separate fabric inherits it.
 */
export function selectedValueForGroup(
	group: MaterialGroup,
	map: FamilyAssetMap | null,
	productId: string | undefined,
): { valueSlug: string; dimensionSlug: string } | undefined {
	const groupSlug = slugify(group.selectorType);
	const productSlugs = productId && map ? map.products[productId] : undefined;

	const own = productSlugs?.[groupSlug];
	if (own) return { valueSlug: own, dimensionSlug: groupSlug };

	if (productSlugs && /fabric/i.test(group.selectorType)) {
		const seat = productSlugs[SEAT_FABRIC_DIMENSION];
		if (seat) return { valueSlug: seat, dimensionSlug: SEAT_FABRIC_DIMENSION };
	}

	return undefined;
}

/**
 * The authoritative selected value slug for a material group. The local option
 * list (if any) is only a display convenience.
 */
export function selectedValueSlugForGroup(
	group: MaterialGroup,
	map: FamilyAssetMap | null,
	productId: string | undefined,
): string | undefined {
	return selectedValueForGroup(group, map, productId)?.valueSlug;
}

export function selectedOptionForGroup(
	group: MaterialGroup,
	map: FamilyAssetMap | null,
	productId: string | undefined,
): MaterialOption | undefined {
	const valueSlug = selectedValueSlugForGroup(group, map, productId);

	if (valueSlug) {
		const matched = group.options.find(
			(opt) => slugify(opt.name) === valueSlug,
		);
		if (matched) return matched;
	}

	return group.options[group.defaultIndex];
}

// Warn once per slug when an asset-map dimension has textures but no GLB slot
// mapping, so a new dimension silently failing to paint is caught early.
const warnedUnmappedDimensions = new Set<string>();

function warnUnmappedDimensions(map: FamilyAssetMap | null): void {
	const dimensions = map?.assets?.dimensions;
	if (!dimensions) return;

	for (const [slug, dimension] of Object.entries(dimensions)) {
		if (
			slug in MATERIAL_SLOTS_BY_DIMENSION ||
			warnedUnmappedDimensions.has(slug)
		) {
			continue;
		}

		const hasTexture = Object.values(dimension.values).some(
			(value) => value['finish-chip']?.url || value.swatch?.url,
		);
		if (!hasTexture) continue;

		warnedUnmappedDimensions.add(slug);
		console.warn(
			`[product3dConfig] Asset-map dimension "${slug}" has swatch textures but no GLB slot in MATERIAL_SLOTS_BY_DIMENSION; its selections will not paint the model.`,
		);
	}
}

/**
 * Wraps the materials record so the viewer's key lookup matches our slot keys
 * case-insensitively (modelers vary casing, e.g. `mat_Nailheads` vs `mat_seat`).
 * The viewer only reads keys, so a get-trap falling back to a lowercased index
 * is enough.
 */
function caseInsensitiveMaterials(
	materials: Record<string, MaterialTextureConfig>,
): Record<string, MaterialTextureConfig> {
	const byLowerKey = new Map<string, MaterialTextureConfig>();
	for (const [key, value] of Object.entries(materials)) {
		byLowerKey.set(key.toLowerCase(), value);
	}

	return new Proxy(materials, {
		get(target, prop, receiver) {
			const exact = Reflect.get(target, prop, receiver);
			if (exact !== undefined || typeof prop !== 'string') return exact;
			return byLowerKey.get(prop.toLowerCase());
		},
	});
}

/**
 * GLB material names to paint for a group. Material names vary across models
 * (e.g. the seat is `mat_seat` on some, the typo `map_seat` on others), so
 * prefer the real names from discovered slots whose key matches this group's
 * dimension; fall back to the static config names before the model loads.
 */
function resolveGroupSlotNames(
	group: MaterialGroup,
	discoveredSlots: MaterialSlot[] | null | undefined,
): string[] {
	if (discoveredSlots?.length) {
		const groupSlug = slugify(group.selectorType);
		const matched = discoveredSlots
			.filter((slot) => slugify(slot.key) === groupSlug)
			.flatMap((slot) => slot.materialNames)
			.filter((name): name is string => !!name);
		if (matched.length) return matched;
	}
	return group.slots;
}

export function buildModelMaterials(
	config3d: Product3dConfig,
	map: FamilyAssetMap | null,
	productId: string | undefined,
	fabricOverride?: FabricOverride,
	discoveredSlots?: MaterialSlot[] | null,
): Record<string, MaterialTextureConfig> {
	warnUnmappedDimensions(map);

	const staticMaterials = config3d.staticMaterials ?? {};
	const materials: Record<string, MaterialTextureConfig> = {
		...staticMaterials,
	};

	for (const group of config3d.materialGroups) {
		// Display-only fabric preview: paint fabric groups straight from the
		// selected swatch. Back slots use backValueSlug when diverged, else mirror
		// the seat.
		if (fabricOverride && /fabric/i.test(group.selectorType)) {
			const isBack = slugify(group.selectorType) === BACK_FABRIC_DIMENSION;
			const overrideValueSlug =
				isBack && fabricOverride.backValueSlug
					? fabricOverride.backValueSlug
					: fabricOverride.seatValueSlug;
			const overrideTexture = fabricTextureFromDimension(
				fabricOverride.dimension,
				overrideValueSlug,
			);
			// A selected value with no swatch falls through to normal resolution
			// rather than blanking the slot.
			if (overrideTexture) {
				for (const slot of resolveGroupSlotNames(group, discoveredSlots)) {
					if (slot in staticMaterials) continue;
					materials[slot] = overrideTexture;
				}
				continue;
			}
		}

		// Value slug comes from the asset map; a matched local option only supplies
		// a texture base. A fabric group with no selection inherits the seat fabric
		// (see selectedValueForGroup).
		const option = selectedOptionForGroup(group, map, productId);
		const groupSlug = slugify(group.selectorType);

		let valueSlug: string | undefined;
		let dimensionSlug: string;
		if (option) {
			valueSlug = slugify(option.name);
			dimensionSlug = groupSlug;
		} else {
			const resolved = selectedValueForGroup(group, map, productId);
			valueSlug = resolved?.valueSlug;
			dimensionSlug = resolved?.dimensionSlug ?? groupSlug;
		}
		if (!valueSlug) continue;

		const assetValue =
			map?.assets?.dimensions?.[dimensionSlug]?.values?.[valueSlug];
		const cdnUrl = chipUrl(assetValue);
		// A pinned tile size (FIXED_REPEAT_INCHES_BY_DIMENSION) wins over the map's
		// uv, which can be wrong for a material (e.g. sub-inch nailheads).
		const uv =
			FIXED_REPEAT_INCHES_BY_DIMENSION[dimensionSlug] ??
			assetValue?.['finish-chip']?.uv ??
			assetValue?.swatch?.uv;

		let texture: MaterialTextureConfig | undefined;
		if (cdnUrl && option?.textureInput) {
			// Local option is the base; the map's CDN url and uv take precedence.
			texture = {
				...option.textureInput,
				url: cdnUrl,
				...(uv ? { repeatWidthInches: uv.w, repeatHeightInches: uv.h } : {}),
			};
		} else if (cdnUrl) {
			// Built entirely from the map, with a default repeat when uv is absent.
			texture = {
				url: cdnUrl,
				repeatWidthInches: uv?.w ?? FABRIC_REPEAT_FALLBACK_INCHES,
				repeatHeightInches: uv?.h ?? FABRIC_REPEAT_FALLBACK_INCHES,
			};
		} else {
			texture = option?.textureInput;
		}

		if (!texture) continue;

		for (const slot of resolveGroupSlotNames(group, discoveredSlots)) {
			if (slot in staticMaterials) continue;
			materials[slot] = texture;
		}
	}

	return caseInsensitiveMaterials(materials);
}

export function productAttributeToSelectedGaAttributeKey(
	key: string,
): `selected_${string}` | null {
	const slug = key
		.toLowerCase()
		.replace(/[^a-z]+/g, '_')
		.replace(/_+/g, '_')
		.replace(/^_+|_+$/g, '');

	if (!slug) return null;

	return `selected_${slug}`;
}
